import clsx from 'clsx';
import React, { FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import { Loading } from '../../common/Loading';
import { IconButton } from '../../common/ui-kit/IconButton';
import { StateSettings, WindowState } from '../GlobalForms.types';

import styles from './PopupWindow.module.scss';
import ExpandIcon from '@lmt/unicorn-shared/app/icons/expand.svg?react';
import CompactIcon from '@lmt/unicorn-shared/app/icons/compact.svg?react';
import HideIcon from '@lmt/unicorn-shared/app/icons/hide.svg?react';
import ShowIcon from '@lmt/unicorn-shared/app/icons/show.svg?react';
import CloseIcon from '@lmt/unicorn-shared/app/icons/close.svg?react';

interface PopupWindowProps {
  id: number;
  title: string;
  tryClose: (id: number) => void;
  state: WindowState;
  isHidden: boolean;
  setIsHidden: (id: number, isHidden: boolean) => void;
  changeState: (id: number, newState: WindowState) => void;
  isBeingCompleted: boolean;
  stateSettings: StateSettings;
  isFocused: boolean;
  isLoading: boolean;
  loadingTitle: string;
  isCloseButtonHidden: boolean;
}

export const PopupWindow: FC<React.PropsWithChildren<PopupWindowProps>> = (
  props,
) => {
  const {
    id,
    title,
    tryClose,
    state,
    isHidden,
    setIsHidden,
    changeState,
    isBeingCompleted,
    stateSettings,
    isFocused,
    isLoading,
    loadingTitle,
    isCloseButtonHidden,
  } = props;

  const currentState = useRef<WindowState>();
  const previousState = useRef<WindowState>();

  useEffect(() => {
    if (currentState.current !== state) {
      if (currentState.current) {
        previousState.current = currentState.current;
      }
      currentState.current = state;
    }
  }, [state]);

  const close = useCallback(() => tryClose(id), [tryClose, id]);
  useEffect(
    function escapeButtonHandler() {
      if (isFocused && !isCloseButtonHidden) {
        const escListener = (e: KeyboardEvent) => {
          if (e.code === 'Escape') {
            if (!isBeingCompleted) {
              close();
            }
          }
        };
        document.addEventListener('keydown', escListener);
        return () => {
          document.removeEventListener('keydown', escListener);
        };
      }
      return;
    },
    [isFocused, isBeingCompleted, close, isCloseButtonHidden],
  );

  const iconButton = useCallback(
    (
      icon: ReactNode,
      disabled: boolean,
      onClick: () => void,
      testId: string,
    ) => (
      <IconButton
        color="light"
        disabled={disabled}
        className={styles.formButton}
        onClick={onClick}
        iconComponent={icon}
        testId={testId}
      />
    ),
    [],
  );

  const isExpanded = state === 'expanded';
  const expandedAndNotHidden = isExpanded && !isHidden;
  const expandButton =
    stateSettings.isExpanded &&
    stateSettings.isNormal &&
    iconButton(
      expandedAndNotHidden ? <CompactIcon /> : <ExpandIcon />,
      false,
      () => {
        changeState(id, expandedAndNotHidden ? 'normal' : 'expanded');
        setIsHidden(id, false);
      },
      expandedAndNotHidden ? 'popup-compactIcon' : 'popup-expandIcon',
    );

  const hideButton =
    stateSettings.isHidden &&
    iconButton(
      isHidden ? <ShowIcon /> : <HideIcon />,
      isBeingCompleted,
      () => setIsHidden(id, !isHidden),
      isHidden ? 'popup-showIcon' : 'popup-hideIcon',
    );

  const closeButton =
    !isCloseButtonHidden &&
    iconButton(
      <CloseIcon className={styles.closeIcon} />,
      isBeingCompleted,
      close,
      'popup-closeIcon',
    );

  return (
    <div
      className={styles.window}
      data-test-id="popup-window"
      data-expanded-and-not-hidden={expandedAndNotHidden}
    >
      <div className={styles.header}>
        <div className={styles.headerStart}>
          <div className={styles.formTitle} data-test-id="popup-window-title">
            {title}
          </div>
        </div>
        <div className={styles.headerEnd}>
          <div className={styles.formButtons}>
            {expandButton}
            {hideButton}
            {closeButton}
          </div>
        </div>
      </div>

      <Loading loading={isLoading} title={loadingTitle}>
        <div
          className={clsx(styles.body, isHidden && styles.hidden)}
          data-test-id="popup-window-content"
        >
          {props.children}
        </div>
      </Loading>
    </div>
  );
};
